import { ClockOutlineIcon } from "@images/svgIcons/clockOutlineIcon"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { ProgramInfinite } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOff } from "@modules/programs/programOnOffTypes"
import { ProgramSubscriberDetailsLayout } from "@modules/programs/programSubscriber/components/programSubscriberDetails/programSubscriberDetailsLayout"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { ProgramSubscriptionStatus } from "@modules/programs/programTypes"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { HURow } from "@modules/ui/components/layout"
import dayjs from "dayjs"
import { ChevronRight } from "lucide-react"
import { memo } from "react"
import styled, { useTheme } from "styled-components"

export const ProgramSubscriberItem: React.FC<{
  program: ProgramInfinite | ProgramOnOff | ProgramBundleEntity
  subscriber: ProgramSubscriberEntity | ProgramBundleSubscriberEntity
  canSeeSubscriberDetails: boolean
}> = memo(({ program, subscriber, canSeeSubscriberDetails }) => {
  const theme = useTheme()
  const toast = useToast()
  const { sidebarNavigateTo, setCurrentStackState } = useSidebar()

  const openSubscriberDetails = () => {
    const sidebarContent = document.getElementById("sidebar-content")
    if (sidebarContent) {
      setCurrentStackState("subscribersListScrollPosition", sidebarContent.scrollTop)
    }

    if (
      program instanceof ProgramBundleEntity &&
      subscriber instanceof ProgramBundleSubscriberEntity &&
      !subscriber.programBundleSubscriptionId
    ) {
      toast.show({
        summary: word("global.error.label"),
        detail: word("program.subscribers.toast.invalidProgramOrSubscriberType.error"),
        severity: "error",
      })
      throw new Error("Invalid program or subscriber type")
    }

    sidebarNavigateTo({
      content: (
        <ProgramSubscriberDetailsLayout
          subscriberId={
            subscriber instanceof ProgramBundleSubscriberEntity ? subscriber.programBundleSubscriptionId : subscriber.id
          }
          program={program}
        />
      ),
      onHide: () => {
        return Promise.resolve(true)
      },
    })
  }

  return (
    <div
      key={subscriber.id}
      className={
        "flex flex-row mb-3 align-items-center justify-content-between" +
        (canSeeSubscriberDetails ? " cursor-pointer" : "")
      }
      onClick={canSeeSubscriberDetails ? openSubscriberDetails : undefined}
      id={"subscriber" + subscriber.id}
    >
      <div className="flex flex-row align-items-center">
        <ProfileAvatar
          className="mr-2"
          profile={{ avatar: subscriber.avatar, username: subscriber.firstname || "" }}
          size={55}
        />
        <div className="flex flex-column justify-content-center gap-1" >
          <HUText fontStyle="LL">{`${subscriber.firstname} ${subscriber.lastname}`}</HUText>
          {subscriber.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT ? (
            <PaymentTag gap={4} $color={theme.neutralColor100}>
              <ClockOutlineIcon color={theme.neutralColor800} width={12} height={12} />
              <HUText fontStyle="LM">{word("program.subscribers.tag.pending_payment")}</HUText>
            </PaymentTag>
          ) : subscriber.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION ? (
            <PaymentTag gap={4} $color={theme.error100}>
              <WarningIcon color={theme.error} width={12} height={12} />
              <HUText fontStyle="LM">
                {word("program.subscribers.tag.pending_cancellation", {
                  endDate: dayjs(subscriber.periodEndDate).diff(dayjs(), "days"),
                })}
              </HUText>
            </PaymentTag>
          ) : subscriber.subscriptionStatus === ProgramSubscriptionStatus.TRIAL_PENDING_CANCELLATION ? (
            <PaymentTag gap={4} $color={theme.error100}>
              <WarningIcon color={theme.error} width={12} height={12} />
              <HUText fontStyle="LM">
                {word("program.subscribers.tag.pending_cancellation", {
                  endDate: dayjs(subscriber.trial?.endDate).diff(dayjs(), "days"),
                })}
              </HUText>
            </PaymentTag>
          ) : (
            <HUText fontStyle="BS" color={theme.neutralColor600}>
              {`@${subscriber.username}`}
            </HUText>
          )}
        </div>
      </div>

      {canSeeSubscriberDetails && (
        <div className="flex justify-content-end">
          <HUButton
            type="Rounded"
            colorType="Tertiary"
            size="XS"
            icon={{ iconView: <ChevronRight size={20} /> }}
            title={word("program.subscribers.seeSubscriptionDetails")}
          />
        </div>
      )}
    </div>
  )
})

const PaymentTag = styled(HURow)<{ $color: string }>`
  padding: 4px 10px 4px 8px;
  align-items: center !important;
  background-color: ${({ $color }) => $color};
  border-radius: 100px;
`
